/**
 * SVG hex board — draws all 295 cells + overlays (active crates, placed
 * landmarks, placed bridges, drop-stacks, pirates, boats, stash markers).
 *
 * Axial → pixel projection is pointy-top per spec/ui-spec/BOARD_RENDERING.md.
 * Pointy-top: x = size*sqrt(3)*(q + r/2), y = size*1.5*r.
 */

import { useMemo } from "react";
import { axialToPixel, hexKey, hexPolygonPoints } from "../hex.js";
import type { Board, BoardCell, Terrain } from "../boardBuild.js";
import type { RenderState } from "../stateReplayer.js";

const HEX_SIZE = 22;

const TERRAIN_COLOR: Record<Terrain, string> = {
  grass: "#7fbd6d",
  beach: "#edd9a3",
  water: "#5a99cc",
  sand: "#ecd3a1",
  rock: "#a89f92",
  swamp: "#6f7a4a",
};

const WATER_LAYER_COLOR: Record<1 | 2 | 3, string> = {
  1: "#5a99cc",
  2: "#4d8dbf",
  3: "#3f7fac",
};

const COLOR_PALETTE: Record<string, string> = {
  red: "#e6533a",
  pink: "#e58ba9",
  blue: "#3f83e6",
  purple: "#8a4ee6",
  white: "#f5f5f5",
  black: "#232323",
  green: "#4aae44",
};

export interface HexBoardProps {
  board: Board;
  state: RenderState;
  showCoords?: boolean;
  highlightCells?: string[];
}

export interface BoardViewBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  margin: number;
  width: number;
  height: number;
  dx: number;
  dy: number;
}

export function computeBoardViewBox(board: Board): BoardViewBox {
  const cells = Array.from(board.cells.values());
  const projected = cells.map((c) => ({
    ...axialToPixel({ q: c.q, r: c.r }, HEX_SIZE),
  }));
  const minX = Math.min(...projected.map((p) => p.x));
  const minY = Math.min(...projected.map((p) => p.y));
  const maxX = Math.max(...projected.map((p) => p.x));
  const maxY = Math.max(...projected.map((p) => p.y));
  const margin = HEX_SIZE * 2;
  return {
    minX,
    minY,
    maxX,
    maxY,
    margin,
    width: maxX - minX + margin * 2,
    height: maxY - minY + margin * 2,
    dx: -minX + margin,
    dy: -minY + margin,
  };
}

export function HexBoard({
  board,
  state,
  showCoords = false,
  highlightCells = [],
}: HexBoardProps) {
  const highlightSet = useMemo(
    () => new Set(highlightCells),
    [highlightCells],
  );
  const viewBox = computeBoardViewBox(board);
  const cells = Array.from(board.cells.values());
  const projected = cells.map((c) => ({
    cell: c,
    ...axialToPixel({ q: c.q, r: c.r }, HEX_SIZE),
  }));

  const droppedByKey = new Map(state.dropped_items.map((d) => [d.cell_key, d.items]));
  const stashByKey = new Map<string, string>(); // cell_key → owner color
  for (const p of state.players) {
    for (const s of p.stashes) {
      stashByKey.set(s.cell_key, p.color);
    }
  }
  const activeCrateSet = new Set(state.active_crates);
  const bridgeSet = new Set(state.placed_bridges);
  const landmarkByKey = new Map(
    state.placed_landmarks.map((l) => [l.cell_key, l.landmark_id]),
  );

  return (
    <svg
      role="img"
      aria-label="Book of Buried Treasure replay board"
      viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      <rect width={viewBox.width} height={viewBox.height} fill="#dfeef8" />
      {projected.map(({ cell, x, y }) => (
        <HexCell
          key={cell.key}
          cell={cell}
          cx={x + viewBox.dx}
          cy={y + viewBox.dy}
          size={HEX_SIZE}
          hasBridge={bridgeSet.has(cell.key)}
          activeCrate={activeCrateSet.has(cell.key)}
          placedLandmark={landmarkByKey.get(cell.key)}
          droppedItems={droppedByKey.get(cell.key)}
          stashOwnerColor={stashByKey.get(cell.key)}
          highlighted={highlightSet.has(cell.key)}
          showCoords={showCoords}
        />
      ))}

      {/* Boats and pirates on top */}
      {state.players.map((p) => {
        const boat = axialToPixel(p.boat_position, HEX_SIZE);
        const pirate = axialToPixel(p.pirate.position, HEX_SIZE);
        const bx = boat.x + viewBox.dx;
        const by = boat.y + viewBox.dy;
        const px = pirate.x + viewBox.dx;
        const py = pirate.y + viewBox.dy;
        const color = COLOR_PALETTE[p.color] ?? "#333";
        return (
          <g key={p.id}>
            {/* Boat as small triangle */}
            <polygon
              points={`${bx - 8},${by + 4} ${bx + 8},${by + 4} ${bx},${by - 8}`}
              fill={color}
              stroke="#222"
              strokeWidth={1}
              opacity={p.pirate.in_boat ? 1 : 0.55}
            />
            {/* Pirate as filled circle offset when in boat */}
            <circle
              cx={p.pirate.in_boat ? px + 6 : px}
              cy={p.pirate.in_boat ? py - 6 : py}
              r={7}
              fill={color}
              stroke="#111"
              strokeWidth={1.5}
            />
            <text
              x={p.pirate.in_boat ? px + 6 : px}
              y={p.pirate.in_boat ? py - 3 : py + 3}
              textAnchor="middle"
              fontSize={9}
              fill={p.color === "white" ? "#111" : "#fff"}
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              {p.id[0]!.toUpperCase()}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function HexCell(props: {
  cell: BoardCell;
  cx: number;
  cy: number;
  size: number;
  hasBridge: boolean;
  activeCrate: boolean;
  placedLandmark: string | undefined;
  droppedItems: string[] | undefined;
  stashOwnerColor: string | undefined;
  highlighted: boolean;
  showCoords: boolean;
}) {
  const {
    cell,
    cx,
    cy,
    size,
    hasBridge,
    activeCrate,
    placedLandmark,
    droppedItems,
    stashOwnerColor,
    highlighted,
    showCoords,
  } = props;

  const fill =
    cell.kind === "cloth_water" && cell.waterLayer !== undefined
      ? WATER_LAYER_COLOR[cell.waterLayer]
      : TERRAIN_COLOR[cell.terrain];
  const points = hexPolygonPoints(cx, cy, size);
  const isBoundary = cell.isOuterBoundary === true;

  return (
    <g>
      <polygon
        points={points}
        fill={hasBridge ? "#a06a3e" : fill}
        stroke={
          highlighted
            ? "#f9d64a"
            : isBoundary
              ? "#274b6b"
              : cell.kind === "cloth_water"
                ? "#3a6a90"
                : "#365"
        }
        strokeWidth={highlighted ? 2.5 : isBoundary ? 1.6 : 0.8}
      />
      {cell.site === "crate" && !activeCrate && (
        <rect
          x={cx - 5}
          y={cy - 5}
          width={10}
          height={10}
          fill="#7a4d1e"
          stroke="#2b1a08"
          strokeWidth={0.8}
          opacity={0.55}
        />
      )}
      {activeCrate && (
        <rect
          x={cx - 7}
          y={cy - 7}
          width={14}
          height={14}
          fill="#c48a3d"
          stroke="#3f2410"
          strokeWidth={1.4}
        />
      )}
      {cell.site === "landmark" && !placedLandmark && (
        <polygon
          points={`${cx},${cy - 6} ${cx + 5},${cy + 4} ${cx - 5},${cy + 4}`}
          fill="#c73030"
          stroke="#2b0505"
          strokeWidth={0.6}
          opacity={0.55}
        />
      )}
      {placedLandmark && (
        <g>
          <polygon
            points={`${cx},${cy - 9} ${cx + 7},${cy + 6} ${cx - 7},${cy + 6}`}
            fill="#c73030"
            stroke="#340000"
            strokeWidth={1.4}
          />
          <text
            x={cx}
            y={cy + 4}
            textAnchor="middle"
            fontSize={7}
            fill="#fff"
            style={{ pointerEvents: "none", userSelect: "none" }}
          >
            {placedLandmark.slice(0, 3)}
          </text>
        </g>
      )}
      {droppedItems && droppedItems.length > 0 && (
        <circle
          cx={cx + 8}
          cy={cy + 8}
          r={4}
          fill="#f7d97a"
          stroke="#3a2a05"
          strokeWidth={0.8}
        />
      )}
      {stashOwnerColor && (
        <circle
          cx={cx - 8}
          cy={cy + 8}
          r={3.5}
          fill={COLOR_PALETTE[stashOwnerColor] ?? "#333"}
          stroke="#111"
          strokeWidth={0.7}
        />
      )}
      {showCoords && (
        <text
          x={cx}
          y={cy + 3}
          textAnchor="middle"
          fontSize={7}
          fill="#111"
          opacity={0.45}
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {cell.q},{cell.r}
        </text>
      )}
    </g>
  );
}
